/** @jsx jsx */

import { jsx, keyframes } from '@emotion/react';
import { labelStyles, withModifier } from '@zapier/style-encapsulation';
import { ColorsCSS } from '../../../theme';
import { VisuallyHidden } from '../VisuallyHidden';

type Props = {
  /** Accessibility label for the node */
  ariaLabel?: string;

  /** Diameter and border width of loader */
  size?: 'small' | 'medium' | 'large';

  /** Should the loader be overlaid on top of a background
   *  NOTE: 'white' and 'black' are deprecated for v5.
   */
  color?: 'white' | 'black' | 'light' | 'dark';
};

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const sizes = {
  small: {
    diameter: 'var(--zds-size-small, 20px)',
    thickness: 4,
  },
  medium: {
    diameter: 'var(--zds-size-medium, 30px)',
    thickness: 6,
  },
  large: {
    diameter: 'var(--zds-size-large, 60px)',
    thickness: 10,
  },
};

const mapPropToColorValue = (color: Props['color']) => {
  let returnedColor;
  switch (color) {
    case 'black':
      returnedColor = ColorsCSS.neutral800;
      break;
    case 'white':
      returnedColor = ColorsCSS.neutral100;
      break;
    case 'light':
      returnedColor = ColorsCSS.neutral100;
      break;
    case 'dark':
      returnedColor = ColorsCSS.neutral800;
  }
  return returnedColor;
};

const Styles = labelStyles('Spinner', {
  root: (props: Required<Props>) => [
    {
      animation: `${spin} 0.75s linear infinite`,
      borderStyle: 'solid',
      borderRightColor: 'transparent',
      borderRadius: '50%',
      opacity: 0.7,
    },
    withModifier(props.size, {
      height: sizes[props.size].diameter,
      width: sizes[props.size].diameter,
      borderWidth: `${sizes[props.size].thickness}px`,
    }),
    withModifier(props.color, {
      borderColor: mapPropToColorValue(props.color),
      borderRightColor: 'transparent',
    }),
  ],
});

/**
 * Renders a spinner that indicates that something async is happening.
 */
export const Spinner = ({
  ariaLabel = 'Loading',
  color = 'black',
  size = 'small',
}: Props) => {
  const props = { ariaLabel, color, size };

  return (
    <div css={Styles.root(props)} data-testid="spinner" role="status" data-zds>
      <VisuallyHidden>{ariaLabel}</VisuallyHidden>
    </div>
  );
};
